/**
 * FlowVisualization - Main flow diagram component
 *
 * Combines all subcomponents to render the complete Sankey-style
 * money flow visualization with three columns:
 * - Inflows (income sources) on the left
 * - Accounts in the middle
 * - Outflows (expense categories) on the right
 *
 * Phase 3: Interactivity - tooltips, click handlers, and expansion menus.
 */

import { memo, useMemo, useState, useCallback } from 'react';
import type {
  InflowNode,
  AccountNode,
  OutflowNode,
  FlowEdge,
  FlowNode,
  LumpedNode,
  TransferData,
  TransferSankeyData,
  TransferSankeyNode,
  TransferSankeyEdge,
} from './types';
import { isInflowNode, isOutflowNode, isAccountNode, isLumpedNode } from './types';
import { CANVAS } from './constants';
import {
  layoutInflowNodes,
  layoutAccountNodes,
  layoutOutflowNodes,
  routeEdges,
  calculateRequiredHeight,
  calculateAllStrokeWidths,
  calculateMinNodeHeightsFromEdges,
} from './layout';
import { FlowCanvas } from './FlowCanvas';
import { InflowColumn } from './InflowColumn';
import { AccountColumn } from './AccountColumn';
import { OutflowColumn } from './OutflowColumn';
import { EdgeLayer } from './EdgeLayer';
import { FlowTooltip, type TooltipContent } from './FlowTooltip';
import { ExpansionMenu } from './ExpansionMenu';
import { TransferSection } from './TransferSection';
import { TransferSankey } from './TransferSankey';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';

// Import mock data for testing
import {
  mockInflowNodes,
  mockAccountNodes,
  mockOutflowNodes,
  mockEdges,
} from './mockData';

/**
 * Props for FlowVisualization component
 */
export interface FlowVisualizationComponentProps {
  // Data props (optional - uses mock data if not provided)
  inflowNodes?: InflowNode[];
  accountNodes?: AccountNode[];
  /** Pre-lumped nodes from backend (used when tiers are not expanded) */
  lumpedNodes?: LumpedNode[];
  outflowNodes?: OutflowNode[];
  edges?: FlowEdge[];
  /** @deprecated Use transferSankey instead (WP-FLOW-REFACTOR-2) */
  transfers?: TransferData[];
  /** WP-FLOW-REFACTOR-2: Transfer Sankey data for visualization */
  transferSankey?: TransferSankeyData;

  // State props
  expandedTiers?: string[];
  isLoading?: boolean;

  // Period information for navigation
  periodStart?: string;
  periodEnd?: string;

  // Callback props
  onNodeClick?: (node: FlowNode) => void;
  onEdgeClick?: (edge: FlowEdge) => void;
  onTierExpand?: (tier: string) => void;

  // Navigation callbacks
  /** WP-FLOW-4: nodeType enables filtering by income/expense transactions */
  onCategoryClick?: (categoryId: string, startDate: string, endDate: string, nodeType: 'income' | 'expense') => void;
  onAccountClick?: (accountId: string, startDate: string, endDate: string) => void;
  onEdgeTransactionClick?: (sourceId: string, targetId: string, startDate: string, endDate: string) => void;
  /** Callback when a transfer is clicked - navigates to filtered transactions.
   * WP-TRANSFER-2: IDs may be null for external/untracked accounts */
  onTransferClick?: (sourceId: string | null, targetId: string | null, startDate: string, endDate: string) => void;

  // Styling
  className?: string;
}

function FlowVisualizationComponent({
  inflowNodes = mockInflowNodes,
  accountNodes = mockAccountNodes,
  lumpedNodes = [],
  outflowNodes = mockOutflowNodes,
  edges = mockEdges,
  transfers = [],
  transferSankey,
  expandedTiers = [],
  isLoading = false,
  periodStart,
  periodEnd,
  onNodeClick,
  onEdgeClick,
  onTierExpand,
  onCategoryClick,
  onAccountClick,
  onEdgeTransactionClick,
  onTransferClick,
  className,
}: FlowVisualizationComponentProps) {
  // Track expanded tiers locally if no external state provided
  const [localExpandedTiers, setLocalExpandedTiers] = useState<string[]>(expandedTiers);
  const effectiveExpandedTiers = expandedTiers.length > 0 ? expandedTiers : localExpandedTiers;

  // Tooltip state
  const [tooltip, setTooltip] = useState<TooltipContent | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Expansion menu state
  const [expansionMenu, setExpansionMenu] = useState<{
    node: LumpedNode;
    position: { x: number; y: number };
  } | null>(null);

  // Highlighted element state
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [highlightedEdgeId, setHighlightedEdgeId] = useState<string | null>(null);

  // ============================================================================
  // Two-Pass Layout Algorithm (prevents edge overflow)
  // ============================================================================
  //
  // Pass 1: Pre-calculate edge stroke widths before laying out nodes
  // This allows us to know how much vertical space edges will need
  const edgeStrokeWidths = useMemo(
    () => calculateAllStrokeWidths(edges),
    [edges]
  );

  // Pass 2: Calculate minimum node heights from connected edges
  // Each node must be tall enough to accommodate all edges without overflow
  const minNodeHeights = useMemo(
    () => calculateMinNodeHeightsFromEdges(edges, edgeStrokeWidths),
    [edges, edgeStrokeWidths]
  );

  // Pass 3: Layout nodes with edge-aware minimum heights
  const positionedInflowNodes = useMemo(
    () => layoutInflowNodes(inflowNodes, CANVAS.HEIGHT, minNodeHeights),
    [inflowNodes, minNodeHeights]
  );

  const positionedAccountNodes = useMemo(
    () => layoutAccountNodes(accountNodes, effectiveExpandedTiers, CANVAS.HEIGHT, minNodeHeights, lumpedNodes),
    [accountNodes, effectiveExpandedTiers, minNodeHeights, lumpedNodes]
  );

  const positionedOutflowNodes = useMemo(
    () => layoutOutflowNodes(outflowNodes, CANVAS.HEIGHT, minNodeHeights),
    [outflowNodes, minNodeHeights]
  );

  // Combine all positioned nodes for edge routing
  const allPositionedNodes = useMemo(
    () => [
      ...positionedInflowNodes,
      ...positionedAccountNodes,
      ...positionedOutflowNodes,
    ],
    [positionedInflowNodes, positionedAccountNodes, positionedOutflowNodes]
  );

  // Pass 4: Route edges using pre-calculated stroke widths
  // We pass undefined for slots to let routeEdges recalculate them with the new node positions
  const positionedEdges = useMemo(
    () => routeEdges(edges, allPositionedNodes, undefined, undefined, edgeStrokeWidths),
    [edges, allPositionedNodes, edgeStrokeWidths]
  );

  // Calculate required height based on actual node positions
  // This ensures the SVG expands to fit all nodes (no clipping)
  const requiredHeight = useMemo(
    () =>
      calculateRequiredHeight(
        positionedInflowNodes,
        positionedAccountNodes,
        positionedOutflowNodes
      ),
    [positionedInflowNodes, positionedAccountNodes, positionedOutflowNodes]
  );

  // Calculate totals for percentage display
  const totalIncome = useMemo(
    () => inflowNodes.reduce((sum, n) => sum + n.value, 0),
    [inflowNodes]
  );
  const totalExpense = useMemo(
    () => outflowNodes.reduce((sum, n) => sum + n.value, 0),
    [outflowNodes]
  );

  // Handle node hover for tooltip
  const handleNodeHover = useCallback(
    (node: FlowNode | null, event?: React.MouseEvent) => {
      if (!node || !event) {
        setTooltip(null);
        setHighlightedNodeId(null);
        return;
      }

      setHighlightedNodeId(node.id);
      setTooltipPosition({ x: event.clientX, y: event.clientY });

      // Build tooltip content based on node type
      if (isInflowNode(node)) {
        setTooltip({
          type: 'node',
          nodeType: 'income',
          title: node.name,
          value: node.value,
          percentage: totalIncome > 0 ? (node.value / totalIncome) * 100 : 0,
        });
      } else if (isOutflowNode(node)) {
        setTooltip({
          type: 'node',
          nodeType: 'expense',
          title: node.name,
          value: node.value,
          percentage: totalExpense > 0 ? (node.value / totalExpense) * 100 : 0,
        });
      } else if (isAccountNode(node)) {
        // WP-FLOW-1: Pass both balance and flowTotal for account tooltips
        // Balance is the primary display value, flowTotal shows period activity
        setTooltip({
          type: 'node',
          nodeType: 'account',
          title: node.name,
          value: node.balance, // Primary: balance
          balance: node.balance,
          flowTotal: node.flowTotal, // Secondary: period activity
        });
      } else if (isLumpedNode(node)) {
        // WP-FLOW-1: Use lumped_account nodeType for balance-focused display
        setTooltip({
          type: 'node',
          nodeType: 'lumped_account',
          title: node.name,
          value: node.totalBalance,
          balance: node.totalBalance,
          accountCount: node.containedAccounts.length,
          // Aggregate flow total from contained accounts
          flowTotal: node.aggregateInflow + node.aggregateOutflow,
        });
      }
    },
    [totalIncome, totalExpense]
  );

  // Handle edge hover for tooltip
  const handleEdgeHover = useCallback(
    (edge: FlowEdge | null, event?: React.MouseEvent) => {
      if (!edge || !event) {
        setTooltip(null);
        setHighlightedEdgeId(null);
        return;
      }

      setHighlightedEdgeId(edge.id);
      setTooltipPosition({ x: event.clientX, y: event.clientY });

      // Find source and target node names
      const sourceNode = allPositionedNodes.find((pn) => pn.node.id === edge.source);
      const targetNode = allPositionedNodes.find((pn) => pn.node.id === edge.target);

      setTooltip({
        type: 'edge',
        source: sourceNode?.node?.name || edge.source,
        target: targetNode?.node?.name || edge.target,
        value: edge.value,
      });
    },
    [allPositionedNodes]
  );

  // Handle node click with navigation and tier expansion
  const handleNodeClick = useCallback(
    (node: FlowNode) => {
      // Check if this is a lumped node that opens the expansion menu
      if (isLumpedNode(node)) {
        // Calculate position for menu (to the right of the node)
        const positionedNode = positionedAccountNodes.find((pn) => pn.node.id === node.id);
        if (positionedNode) {
          // Get the SVG element's bounding rect for positioning
          // Note: .flow-canvas IS the SVG element itself (not a container)
          const svgElement = document.querySelector('svg.flow-canvas');
          if (svgElement) {
            const svgRect = svgElement.getBoundingClientRect();
            setExpansionMenu({
              node,
              position: {
                x: svgRect.left + positionedNode.x + positionedNode.width + 10,
                y: svgRect.top + positionedNode.y,
              },
            });
          }
        }
        return;
      }

      // Handle navigation for income/expense categories
      // WP-FLOW-4: Pass nodeType to filter transactions by sign
      if (isInflowNode(node)) {
        const categoryId = node.categoryId;
        if (onCategoryClick && periodStart && periodEnd) {
          onCategoryClick(categoryId, periodStart, periodEnd, 'income');
        }
      } else if (isOutflowNode(node)) {
        const categoryId = node.categoryId;
        if (onCategoryClick && periodStart && periodEnd) {
          onCategoryClick(categoryId, periodStart, periodEnd, 'expense');
        }
      }

      // Handle navigation for accounts
      if (isAccountNode(node)) {
        const accountId = node.accountId;
        if (onAccountClick && periodStart && periodEnd) {
          onAccountClick(accountId, periodStart, periodEnd);
        }
      }

      // Call external handler if provided
      onNodeClick?.(node);
    },
    [onNodeClick, onCategoryClick, onAccountClick, periodStart, periodEnd, positionedAccountNodes]
  );

  // Handle edge click for navigation
  const handleEdgeClick = useCallback(
    (edge: FlowEdge) => {
      if (onEdgeTransactionClick && periodStart && periodEnd) {
        onEdgeTransactionClick(edge.source, edge.target, periodStart, periodEnd);
      }
      onEdgeClick?.(edge);
    },
    [onEdgeClick, onEdgeTransactionClick, periodStart, periodEnd]
  );

  // Handle expansion menu expand action
  const handleExpand = useCallback(() => {
    if (!expansionMenu) return;

    const tier = expansionMenu.node.tier;
    if (onTierExpand) {
      onTierExpand(tier);
    } else {
      // Toggle local state
      setLocalExpandedTiers((prev) =>
        prev.includes(tier)
          ? prev.filter((t) => t !== tier)
          : [...prev, tier]
      );
    }
  }, [expansionMenu, onTierExpand]);

  // Handle expansion menu account click
  const handleExpansionMenuAccountClick = useCallback(
    (accountId: string) => {
      if (onAccountClick && periodStart && periodEnd) {
        onAccountClick(accountId, periodStart, periodEnd);
      }
    },
    [onAccountClick, periodStart, periodEnd]
  );

  // Handle expansion menu close
  const handleCloseExpansionMenu = useCallback(() => {
    setExpansionMenu(null);
  }, []);

  // Handle transfer click for navigation
  // WP-TRANSFER-2: IDs may be null for external/untracked accounts
  const handleTransferClick = useCallback(
    (sourceId: string | null, targetId: string | null) => {
      if (onTransferClick && periodStart && periodEnd) {
        onTransferClick(sourceId, targetId, periodStart, periodEnd);
      }
    },
    [onTransferClick, periodStart, periodEnd]
  );

  // WP-FLOW-REFACTOR-2: Handle transfer Sankey node click
  const handleTransferNodeClick = useCallback(
    (node: TransferSankeyNode) => {
      if (onTransferClick && periodStart && periodEnd && node.accountId) {
        // Navigate to transactions filtered by this account
        onTransferClick(node.accountId, null, periodStart, periodEnd);
      }
    },
    [onTransferClick, periodStart, periodEnd]
  );

  // WP-FLOW-REFACTOR-2: Handle transfer Sankey edge click
  const handleTransferEdgeClick = useCallback(
    (edge: TransferSankeyEdge) => {
      if (!onTransferClick || !periodStart || !periodEnd || !transferSankey) {
        return;
      }
      // Find the source and target nodes to get their account IDs
      const sourceNode = transferSankey.sourceNodes.find((n) => n.id === edge.sourceNodeId);
      const targetNode = transferSankey.targetNodes.find((n) => n.id === edge.targetNodeId);
      const sourceAccountId = sourceNode?.accountId ?? null;
      const targetAccountId = targetNode?.accountId ?? null;
      onTransferClick(sourceAccountId, targetAccountId, periodStart, periodEnd);
    },
    [onTransferClick, periodStart, periodEnd, transferSankey]
  );

  // Render loading skeleton that mimics the final layout
  if (isLoading) {
    return (
      <div className={cn('flow-visualization', className)}>
        <div
          className="bg-gray-50 rounded-lg overflow-hidden"
          role="status"
          aria-label="Loading flow visualization"
        >
          {/* Skeleton mimics the three-column layout */}
          <div className="p-8 animate-pulse">
            {/* Column headers */}
            <div className="flex justify-between mb-8">
              <div className="h-4 w-16 bg-gray-200 rounded" />
              <div className="h-4 w-20 bg-gray-200 rounded" />
              <div className="h-4 w-18 bg-gray-200 rounded" />
            </div>

            {/* Three column layout */}
            <div className="flex gap-8 justify-between">
              {/* Inflow column skeleton */}
              <div className="flex-1 space-y-4 max-w-[120px]">
                <div className="h-16 bg-gray-200 rounded w-20" />
                <div className="h-12 bg-gray-200 rounded w-20" />
                <div className="h-8 bg-gray-200 rounded w-20" />
                <div className="h-6 bg-gray-200 rounded w-20" />
              </div>

              {/* Accounts column skeleton */}
              <div className="flex-1 space-y-4 max-w-[140px]">
                <div className="h-14 bg-gray-200 rounded w-24" />
                <div className="h-14 bg-gray-200 rounded w-24" />
                <div className="h-14 bg-gray-200 rounded w-24" />
                <div className="h-3 w-full border-t border-dashed border-gray-300 mt-4" />
                <div className="h-16 bg-gray-200 rounded w-24" />
              </div>

              {/* Outflow column skeleton */}
              <div className="flex-1 space-y-4 max-w-[120px]">
                <div className="h-14 bg-gray-200 rounded w-20 ml-auto" />
                <div className="h-10 bg-gray-200 rounded w-20 ml-auto" />
                <div className="h-8 bg-gray-200 rounded w-20 ml-auto" />
                <div className="h-6 bg-gray-200 rounded w-20 ml-auto" />
                <div className="h-5 bg-gray-200 rounded w-20 ml-auto" />
                <div className="h-4 bg-gray-200 rounded w-20 ml-auto" />
              </div>
            </div>
          </div>
          <span className="sr-only">Loading flow visualization...</span>
        </div>
      </div>
    );
  }

  // Calculate period label for accessibility
  const periodLabel = periodStart
    ? new Date(periodStart).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : undefined;

  return (
    <div className={cn('flow-visualization flex flex-col gap-6', className)}>
      {/* Screen reader summary (visually hidden) */}
      <div className="sr-only">
        Flow summary: {formatCurrency(totalIncome)} income from {inflowNodes.length} sources,
        flowing through {accountNodes.length} accounts,
        to {formatCurrency(totalExpense)} in expenses across {outflowNodes.length} categories.
        Net flow: {formatCurrency(totalIncome - totalExpense)}.
      </div>

      {/* Main flow diagram with vertical scroll support for large diagrams */}
      <div className="flow-diagram-container">
        <FlowCanvas
        width={CANVAS.WIDTH}
        height={requiredHeight}
        periodLabel={periodLabel}
        totalIncome={totalIncome}
        totalExpenses={totalExpense}
      >
        {/* Render edges first (below nodes) */}
        <EdgeLayer
          positionedEdges={positionedEdges}
          onEdgeClick={handleEdgeClick}
          onEdgeHover={handleEdgeHover}
          highlightedEdgeId={highlightedEdgeId}
        />

        {/* Render nodes by column */}
        <InflowColumn
          positionedNodes={positionedInflowNodes}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          highlightedNodeId={highlightedNodeId}
        />
        <AccountColumn
          positionedNodes={positionedAccountNodes}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          highlightedNodeId={highlightedNodeId}
          expandedTiers={effectiveExpandedTiers}
        />
        <OutflowColumn
          positionedNodes={positionedOutflowNodes}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          highlightedNodeId={highlightedNodeId}
        />
      </FlowCanvas>
      </div>

      {/* Transfer Sankey - displayed below main flow when transfer data exists */}
      {/* WP-FLOW-REFACTOR-2: Use TransferSankey when available, fallback to TransferSection */}
      {transferSankey && transferSankey.edges.length > 0 ? (
        <TransferSankey
          data={transferSankey}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onNodeClick={handleTransferNodeClick}
          onEdgeClick={handleTransferEdgeClick}
        />
      ) : transfers && transfers.length > 0 ? (
        <TransferSection
          transfers={transfers}
          onTransferClick={handleTransferClick}
        />
      ) : null}

      {/* Tooltip (rendered as portal) */}
      <FlowTooltip content={tooltip} position={tooltipPosition} />

      {/* Expansion menu for lumped nodes (rendered as portal) */}
      {expansionMenu && (
        <ExpansionMenu
          node={expansionMenu.node}
          position={expansionMenu.position}
          onExpand={handleExpand}
          onAccountClick={handleExpansionMenuAccountClick}
          onClose={handleCloseExpansionMenu}
        />
      )}
    </div>
  );
}

export const FlowVisualization = memo(FlowVisualizationComponent);
export default FlowVisualization;
